import React, { JSX } from 'react';

interface State {
  totalPages: number;
  currentPage: number;
  initialNpoData?: string;
  completed: boolean;
}

interface SetTotalPages {
  type: 'SET_TOTAL_PAGES';
  totalPages: number;
}

interface SetCurrentPage {
  type: 'SET_CURRENT_PAGE';
  currentPage: number;
}

interface AddOnePage {
  type: 'ADD_ONE_PAGE';
}

interface ResetCurrentPage {
  type: 'RESET_CURRENT_PAGE';
}

interface SetInitialNpoData {
  type: 'SET_INITIAL_NPO_DATA';
  initialNpoData: string;
}

interface CompleteOnboarding {
  type: 'COMPLETE_ONBOARDING';
}

interface ResetCompleted {
  type: 'RESET_COMPLETED_ONBOARDING';
}

type Action =
  | SetCurrentPage
  | SetTotalPages
  | AddOnePage
  | ResetCurrentPage
  | SetInitialNpoData
  | CompleteOnboarding
  | ResetCompleted;

type Dispatch = (action: Action) => void;

interface NpoLoginScreenStateContextData {
  state: State;
  dispatch: Dispatch;
  setTotalPages: (totalPages: number) => void;
  setCurrentPage: (currentPage: number) => void;
  addOnePage: () => void;
  resetCurrentPage: () => void;
  setInitialNpoData: (initialNpoData: string) => void;
  completeOnboarding: () => void;
  resetCompleted: () => void;
}

const defaultValues: State = {
  totalPages: 12,
  currentPage: 1,
  initialNpoData: undefined,
  completed: false
};

const NpoLoginScreenStateContext = React.createContext<NpoLoginScreenStateContextData | undefined>(undefined);
function npoLoginScreenStateReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.currentPage
      };
    case 'SET_TOTAL_PAGES':
      return {
        ...state,
        totalPages: action.totalPages
      };
    case 'ADD_ONE_PAGE':
      return {
        ...state,
        currentPage: state.currentPage + 1
      };
    case 'RESET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: 1
      };

    case 'SET_INITIAL_NPO_DATA':
      return {
        ...state,
        initialNpoData: action.initialNpoData
      };

    case 'COMPLETE_ONBOARDING':
      return {
        ...state,
        completed: true
      };

    case 'RESET_COMPLETED_ONBOARDING':
      return {
        ...state,
        completed: false
      };

    default:
      throw new Error(`Unhandled action type`);
  }
}

function NpoLoginScreenStateProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(npoLoginScreenStateReducer, {
    totalPages: defaultValues.totalPages,
    currentPage: defaultValues.currentPage,
    initialNpoData: defaultValues.initialNpoData,
    completed: defaultValues.completed
  });

  const setTotalPages = (totalPages: number) => {
    dispatch({
      type: 'SET_TOTAL_PAGES',
      totalPages: totalPages
    });
  };

  const setCurrentPage = (currentPage: number) => {
    dispatch({
      type: 'SET_CURRENT_PAGE',
      currentPage: currentPage
    });
  };

  const addOnePage = () => {
    dispatch({
      type: 'ADD_ONE_PAGE'
    });
  };

  const resetCurrentPage = () => {
    dispatch({
      type: 'RESET_CURRENT_PAGE'
    });
  };

  const setInitialNpoData = (initialNpoData: string) => {
    dispatch({
      type: 'SET_INITIAL_NPO_DATA',
      initialNpoData: initialNpoData
    });
  };

  const completeOnboarding = () => {
    dispatch({
      type: 'COMPLETE_ONBOARDING'
    });
  };

  const resetCompleted = () => {
    dispatch({
      type: 'RESET_COMPLETED_ONBOARDING'
    });
  };

  return (
    <NpoLoginScreenStateContext.Provider
      value={{
        state,
        dispatch,
        setTotalPages,
        setCurrentPage,
        addOnePage,
        resetCurrentPage,
        setInitialNpoData,
        completeOnboarding,
        resetCompleted
      }}>
      {children}
    </NpoLoginScreenStateContext.Provider>
  );
}

function useNpoLoginScreenState(): NpoLoginScreenStateContextData {
  const context = React.useContext(NpoLoginScreenStateContext);
  if (context === undefined) {
    throw new Error('useNpoLoginScreenState must be used within a NpoLoginScreenStateProvider');
  }
  return context;
}

export { NpoLoginScreenStateProvider, useNpoLoginScreenState };
